import React, { useState, createContext, useContext, useEffect } from 'react';
import Header from '../components/landing/Header';
import Hero from '../components/landing/Hero';
import ChatSection from '../components/landing/ChatSection';
import CoOpLeadMagnet from '../components/landing/CoOpLeadMagnet';
import PathOverview from '../components/landing/PathOverview';
import PathStepsStrip from '../components/landing/PathStepsStrip';
import GrantShowcase from '../components/landing/GrantShowcase';
import CorePlatform from '../components/landing/CorePlatform';
import PodShowcase from '../components/landing/PodShowcase';
import VideoPresentation from '../components/landing/VideoPresentation';
import ClientFavoriteService from '../components/landing/ClientFavoriteService';
import CinematicAdSystem from '../components/landing/CinematicAdSystem';
import BestOfBestDirectory from '../components/landing/BestOfBestDirectory';
import StepDetails from '../components/landing/StepDetails';
import FAQ from '../components/landing/FAQ';
import CoOpFundsExplainer from '../components/landing/CoOpFundsExplainer';
import AboutFounder from '../components/landing/AboutFounder';
import FinalCTA from '../components/landing/FinalCTA';
import Footer from '../components/landing/Footer';
import PodPanel, { PodTriggerButton } from '../components/landing/PodPanel';
import PreApprovalWizard from '../components/landing/PreApprovalWizard';
import Testimonials from '../components/landing/Testimonials';
import TrustBanner from '../components/landing/TrustBanner';
import TrustStats from '../components/landing/TrustStats';
import ScrollForMore from '../components/landing/ScrollForMore';
import PresentationViewer from '../components/landing/PresentationViewer';
import FounderVideoSection from '../components/landing/FounderVideoSection';

// Create context for POD panel
export const PodPanelContext = createContext();

// Create context for Pre-Approval Wizard
export const PreApprovalContext = createContext();

export const usePodPanel = () => {
  const context = useContext(PodPanelContext);
  if (!context) {
    throw new Error('usePodPanel must be used within a PodPanelProvider');
  }
  return context;
};

export const usePreApproval = () => {
  const context = useContext(PreApprovalContext);
  if (!context) {
    throw new Error('usePreApproval must be used within a PreApprovalProvider');
  }
  return context;
};

const LandingPage = () => {
  const [isPodPanelOpen, setIsPodPanelOpen] = useState(false);
  const [isPreApprovalOpen, setIsPreApprovalOpen] = useState(false);

  // Ensure page starts at the top on load - robust solution for all devices
  useEffect(() => {
    // Immediate scroll
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    // Delayed scroll for mobile browsers that may have async rendering
    const timeoutId = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Check for payment return - auto-open wizard at correct step
  useEffect(() => {
    const checkPaymentReturn = () => {
      const savedProgress = localStorage.getItem('oneforce_preapproval_progress');
      const pendingAppFee = localStorage.getItem('pendingApplicationFee');
      const pendingFundingFee = localStorage.getItem('pendingFundingPayment');
      
      // If user was at application fee step and is returning
      if (savedProgress) {
        try {
          const parsed = JSON.parse(savedProgress);
          // Step 15 is APPLICATION_FEE - if they were there, they likely paid
          if (parsed.step === 15 || pendingAppFee) {
            // Update to step 16 (BOOK_CALL) and open wizard
            localStorage.setItem('oneforce_preapproval_progress', JSON.stringify({
              ...parsed,
              step: 16, // BOOK_CALL step
              applicationFeePaid: true
            }));
            localStorage.removeItem('pendingApplicationFee');
            localStorage.setItem('applicationFeePaid', 'true');
            // Open wizard after short delay
            setTimeout(() => setIsPreApprovalOpen(true), 500);
            return;
          }
          
          // If they paid funding fee
          if (pendingFundingFee) {
            localStorage.setItem('oneforce_preapproval_progress', JSON.stringify({
              ...parsed,
              step: 20, // VIEW_PRICING step (after funding)
              fundingFeePaid: true
            }));
            localStorage.removeItem('pendingFundingPayment');
            localStorage.setItem('fundingFeePaid', 'true');
            setTimeout(() => setIsPreApprovalOpen(true), 500);
            return;
          }
        } catch (e) {
          console.error('Error checking payment return:', e);
        }
      }
    };
    
    // Run check on mount
    checkPaymentReturn();
  }, []);

  const openPodPanel = () => setIsPodPanelOpen(true);
  const closePodPanel = () => setIsPodPanelOpen(false);
  
  const openPreApproval = () => setIsPreApprovalOpen(true);
  const closePreApproval = () => setIsPreApprovalOpen(false);

  const handlePreApprovalComplete = (data) => {
    console.log('Pre-approval completed:', data);
  };

  return (
    <PodPanelContext.Provider value={{ openPodPanel, closePodPanel, isPodPanelOpen }}>
      <PreApprovalContext.Provider value={{ openPreApproval, closePreApproval, isPreApprovalOpen }}>
        <div className="min-h-screen bg-slate-950">
          <Header />
          <main>
            {/* Hero Section */}
            <Hero />
            
            {/* Interactive Presentation - Full overview of the platform */}
            <PresentationViewer />
            
            {/* Path Steps Strip - Simple visual of 10 steps with checkmarks */}
            <PathStepsStrip />
            
            {/* Founder Video - After You're Funded Message from Roe Hubbard */}
            <FounderVideoSection />
            
            {/* Trust Stats Banner - 20,000+ Happy Clients, $50M+, 4.9/5 */}
            <TrustStats />
            
            {/* AI Chat Section - Ask Your Funding Assistant */}
            <ChatSection />
            
            {/* Step-by-Step Details - Right after AI Funding */}
            <StepDetails />
            
            {/* Scroll for More CTA */}
            <ScrollForMore />
            
            {/* ======= FEATURES DEEP DIVE SECTION ======= */}
            <div id="features-deep-dive">
              {/* $120K Co-Op Lead Magnet - Main value proposition */}
              <CoOpLeadMagnet />
              
              {/* Path Overview - Full visual with details */}
              <PathOverview />
              
              {/* What's Included - Grant Showcase with pie chart */}
              <GrantShowcase />
              
              {/* CRM Platform with POD Integration */}
              <CorePlatform />
              
              {/* POD Showcase - Deep dive into POD */}
              <PodShowcase />
              
              {/* Trust Banner - Security & Guarantees */}
              <TrustBanner />
              
              {/* Video Presentation */}
              <VideoPresentation />
              
              {/* Client Favorite Service - Landing Pages */}
              <ClientFavoriteService />
              
              {/* Cinematic Ad System */}
              <CinematicAdSystem />
              
              {/* Best of Best Directory & Publishing */}
              <BestOfBestDirectory />
            </div>
            
            {/* Testimonials - Client Reviews */}
            <Testimonials />
            
            {/* Co-Op Funds Explainer - What Are Co-Op Funds */}
            <CoOpFundsExplainer />
            
            {/* FAQ */}
            <FAQ />
            
            {/* About Founder */}
            <AboutFounder />
            
            {/* Final CTA */}
            <FinalCTA />
          </main>
          <Footer />
          
          {/* POD Panel and Trigger */}
          <PodTriggerButton onClick={openPodPanel} />
          <PodPanel isOpen={isPodPanelOpen} onClose={closePodPanel} />
          
          {/* Pre-Approval Wizard Modal */}
          <PreApprovalWizard 
            isOpen={isPreApprovalOpen} 
            onClose={closePreApproval}
            onComplete={handlePreApprovalComplete}
            openPodPanel={openPodPanel}
          />
        </div>
      </PreApprovalContext.Provider>
    </PodPanelContext.Provider>
  );
};

export default LandingPage;
